import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  dataLoadFailed,
  dataLoadIdle,
  dataLoadLoading,
  dataLoadReady,
  type DataLoadSnapshot,
} from '../../core/async-load/async-load-state';
import { ApiClientError, userFacingMessage } from '../../core/errors/user-facing-error';
import { ApiClientService } from '../../core/services/api-client.service';
import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MB } from '../../core/constants/upload-limits';
import { readFileAsBase64 } from '../../core/util/file-base64';
import {
  AskConversationDetail,
  AskConversationSummary,
  AskFeedback,
  AskIntent,
  AskReplyLanguage,
  AskResult,
  BatchWithExtractions,
  CaseExtraction,
  ConfirmAction,
  DocumentBatch,
  ExtractedCaseFields,
} from './document-ocr.models';

const ALLOWED_MIME: Record<string, true> = {
  'application/pdf': true,
  'image/jpeg': true,
  'image/jpg': true,
  'image/png': true,
  'image/webp': true,
};

@Injectable({ providedIn: 'root' })
export class DocumentOcrService {
  private readonly api = inject(ApiClientService);

  private readonly items = signal<BatchWithExtractions[]>([]);
  readonly listLoad = signal<DataLoadSnapshot>(dataLoadIdle());
  readonly loading = computed(() => this.listLoad().phase === 'loading');
  readonly error = computed(() => this.listLoad().message);

  readonly batches = () =>
    [...this.items()].sort(
      (a, b) => new Date(b.batch.createdAt).getTime() - new Date(a.batch.createdAt).getTime(),
    );

  getBatchWithExtractions(batchId: number): BatchWithExtractions | undefined {
    return this.items().find((x) => x.batch.id === batchId);
  }

  getExtraction(extractionId: number): CaseExtraction | undefined {
    for (const item of this.items()) {
      const found = item.extractions.find((e) => e.id === extractionId);
      if (found) return found;
    }
    return undefined;
  }

  /** `silent: true` (used by ocr-documents.component's background poll while extraction is
   *  still running) skips touching `loading` — otherwise every poll tick flickers the manual
   *  "Refresh" button to "Refreshing…" and disables it, even though nobody clicked it. */
  async refresh({ silent = false }: { silent?: boolean } = {}): Promise<void> {
    if (!silent) this.listLoad.set(dataLoadLoading(this.listLoad()));
    try {
      const res = await firstValueFrom(this.api.get<{ items: BatchWithExtractions[] }>('/documents/intake'));
      const items = res.items || [];
      this.items.set(items);
      if (!silent) this.listLoad.set(dataLoadReady(items.length > 0));
    } catch (err) {
      if (!silent) this.listLoad.set(dataLoadFailed(err));
    }
  }

  async fetchExtraction(extractionId: number): Promise<CaseExtraction> {
    const extraction = await firstValueFrom(
      this.api.get<CaseExtraction>(`/documents/intake/extractions/${extractionId}`),
    );
    this.applyExtractionUpdate(extraction);
    return extraction;
  }

  async refreshOne(batchId: number): Promise<BatchWithExtractions | undefined> {
    const res = await firstValueFrom(this.api.get<BatchWithExtractions>(`/documents/intake/${batchId}`));
    this.items.update((list) => {
      const rest = list.filter((x) => x.batch.id !== res.batch.id);
      return [res, ...rest];
    });
    return res;
  }

  /** Reads the file as base64 and uploads it — throws if the type isn't supported. */
  async upload(file: File): Promise<DocumentBatch> {
    if (!ALLOWED_MIME[file.type]) {
      throw Object.assign(new Error('FILE_TYPE_NOT_ALLOWED'), { intakeCode: 'FILE_TYPE_NOT_ALLOWED' });
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      throw Object.assign(new Error('FILE_TOO_LARGE'), { intakeCode: 'FILE_TOO_LARGE' });
    }
    const contentBase64 = await readFileAsBase64(file);
    const batch = await firstValueFrom(
      this.api.post<DocumentBatch>('/documents/intake', { mimeType: file.type, contentBase64 }),
    );
    this.items.update((list) => {
      const rest = list.filter((x) => x.batch.id !== batch.id);
      return [{ batch, extractions: [] }, ...rest];
    });
    if (this.listLoad().phase !== 'loading') {
      this.listLoad.set(dataLoadReady(this.items().length > 0));
    }
    return batch;
  }

  async correctExtraction(extractionId: number, patch: Partial<ExtractedCaseFields>): Promise<CaseExtraction> {
    const updated = await firstValueFrom(
      this.api.patch<CaseExtraction>(`/documents/intake/extractions/${extractionId}`, { extractedJson: patch }),
    );
    this.applyExtractionUpdate(updated);
    return updated;
  }

  async confirmExtraction(
    extractionId: number,
    action: ConfirmAction,
    grievanceRef?: number,
    options?: { acknowledgeUnresolvedLocation?: boolean },
  ): Promise<CaseExtraction> {
    const updated = await firstValueFrom(
      this.api.post<CaseExtraction>(`/documents/intake/extractions/${extractionId}/confirm`, {
        action,
        grievanceRef,
        acknowledgeUnresolvedLocation: options?.acknowledgeUnresolvedLocation === true,
      }),
    );
    this.applyExtractionUpdate(updated);
    return updated;
  }

  /** Downloads the original uploaded scan for a batch (same pattern as ops.service.ts's case attachments). */
  async downloadSourceFile(batchId: number, fileName = `case-${batchId}`): Promise<void> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/${batchId}/file`));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  /**
   * Fetches the original scan for inline preview (image or PDF, whichever it is).
   * Caller owns the returned object URL and must revoke it when done (e.g. ngOnDestroy).
   */
  async getSourcePreview(batchId: number): Promise<{ url: string; mimeType: string }> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/${batchId}/file`));
    return { url: URL.createObjectURL(blob), mimeType: blob.type };
  }

  /**
   * Same as getSourcePreview, but cropped to just this one extraction's own pages when the
   * upload had several back-to-back applications — a reviewer should only see the case they're
   * actually reviewing, not the other cases that happened to share the same scan.
   */
  async getExtractionSourcePreview(extractionId: number): Promise<{ url: string; mimeType: string }> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/extractions/${extractionId}/file`));
    return { url: URL.createObjectURL(blob), mimeType: blob.type };
  }

  /** Downloads just this extraction's own pages — see getExtractionSourcePreview. */
  async downloadExtractionSourceFile(extractionId: number, fileName = `case-${extractionId}`): Promise<void> {
    const blob = await firstValueFrom(this.api.getBlob(`/documents/intake/extractions/${extractionId}/file`));
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }

  ask(
    question: string,
    grievanceId?: number | string,
    sessionId?: string,
    replyLanguage?: AskReplyLanguage,
    intent?: AskIntent,
  ): Promise<AskResult> {
    return firstValueFrom(
      this.api.post<AskResult>('/cases/ask', { question, grievanceId, sessionId, replyLanguage, intent }),
    );
  }

  /** Restore logged conversation_json for the Ask AI chat UI. */
  getAskConversation(sessionId: string): Promise<AskConversationDetail | null> {
    const sid = String(sessionId || '').trim();
    if (!sid) return Promise.resolve(null);
    return firstValueFrom(
      this.api.get<AskConversationDetail>(`/cases/ask/sessions/${encodeURIComponent(sid)}`),
    ).catch((err: unknown) => {
      if (err instanceof ApiClientError && err.status === 404) {
        return null;
      }
      throw err;
    });
  }

  /** The caller's own Ask AI chats — pinned first, most-recently-active next. */
  listAskConversations(archived = false): Promise<AskConversationSummary[]> {
    return firstValueFrom(
      this.api.get<{ conversations: AskConversationSummary[] }>('/cases/ask/conversations', { archived }),
    ).then((res) => res.conversations || []);
  }

  /** Rename / pin / unpin / archive / unarchive — pass any subset of these fields. */
  patchAskConversation(
    sessionId: string,
    patch: { title?: string; pinned?: boolean; archived?: boolean },
  ): Promise<AskConversationSummary> {
    return firstValueFrom(
      this.api.patch<AskConversationSummary>(`/cases/ask/conversations/${encodeURIComponent(sessionId)}`, patch),
    );
  }

  /** Empties the Q&A history but keeps the chat (title, pin/archive state). */
  clearAskConversation(sessionId: string): Promise<AskConversationSummary> {
    return firstValueFrom(
      this.api.post<AskConversationSummary>(`/cases/ask/conversations/${encodeURIComponent(sessionId)}/clear`),
    );
  }

  /** Thumbs up/down on one turn (by its index in conversation_json); pass null to clear it. */
  sendAskFeedback(sessionId: string, turnIndex: number, feedback: AskFeedback | null): Promise<AskConversationDetail> {
    return firstValueFrom(
      this.api.post<AskConversationDetail>(`/cases/ask/conversations/${encodeURIComponent(sessionId)}/feedback`, {
        turnIndex,
        feedback,
      }),
    );
  }

  /** Best-effort Hindi transliteration suggestion for the composer — see
   *  backend/src/lib/hindi-transliterate-client.js. Never rejects: any failure (network, upstream
   *  down, timeout) resolves to null, since this is a UI aid, not a core feature. */
  transliterateToHindi(text: string): Promise<string | null> {
    return firstValueFrom(this.api.post<{ text: string | null }>('/cases/ask/transliterate', { text }))
      .then((res) => res.text)
      .catch(() => null);
  }

  deleteAskConversation(sessionId: string): Promise<void> {
    return firstValueFrom(
      this.api.delete<{ sessionId: string; deleted: boolean }>(`/cases/ask/conversations/${encodeURIComponent(sessionId)}`),
    ).then(() => undefined);
  }

  private applyExtractionUpdate(updated: CaseExtraction): void {
    this.items.update((list) =>
      list.map((item) =>
        item.batch.id === updated.batchRef
          ? { ...item, extractions: item.extractions.map((e) => (e.id === updated.id ? updated : e)) }
          : item,
      ),
    );
  }

}
